import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import banners from '@gin-betting/banners';
import core from '@gin-betting/core';
import {
    first,
    flatten,
    forEach,
    get,
    isEmpty,
    keys,
    reduce,
    some,
    transform,
    uniq,
    values,
} from 'lodash';
import { createSelector, createStructuredSelector } from 'reselect';

import { MARKETS_ACTIVE_STATUSES } from 'app-constants';
import { usePureSelector } from 'hooks';
import type { State } from 'types';
import {
    ALL,
    MARKET_LIMIT,
    MOBILE_TOP_MATCH_LIST_INDEX_CONFIG,
} from '../../../constants';
import { makeSelectZones } from '../../../selectors';
import type { BannerItemsByZoneId, WidgetType } from '../../../types';
import { getAttributesByPeriod } from '../../../utils';

const {
    entities: {
        selectors: { selectSportEventIdsFromProps, selectMatches },
    },
    utils: { getBettingOptions },
    hooks: { useDeepMemo },
} = core;

const {
    actions: { getBannersData },
    selectors: { makeSelectBannerZonesByAttributes },
} = banners;

interface Output {
    periods: WidgetType[];
    sportIds: string[];
    missingSportEventIds: string[];
    activeZones: BannerItemsByZoneId | undefined;
    activePeriod?: WidgetType;
    activeSport: string;
    makeHandleActivePeriod: (zoneIndex: WidgetType) => VoidFunction;
    makeHandleChangeActiveSport: (sportId: string) => VoidFunction;
}

const DEFAULT_SPORT = ALL;

const requestedZoneIdsCache: string[] = [];

const attributesByPeriod = getAttributesByPeriod(
    MOBILE_TOP_MATCH_LIST_INDEX_CONFIG
);

const selectMissingSportEventIds = createSelector(
    selectSportEventIdsFromProps,
    selectMatches,
    (sportEventIds, sportEvents) =>
        sportEventIds.filter(id => {
            const sportEvent = sportEvents.get(id);

            return !sportEvent || sportEvent.markets.length < MARKET_LIMIT;
        })
);

const useMobileTopMatchList = (): Output => {
    const dispatch = useDispatch();

    const [activeSport, setActiveSport] = useState<string>(DEFAULT_SPORT);
    const [activePeriod, setActivePeriod] = useState<WidgetType | undefined>();

    const makeHandleActivePeriod = useCallback(
        (zoneIndex: WidgetType) => () => {
            setActivePeriod(zoneIndex);
            setActiveSport(DEFAULT_SPORT);
        },
        []
    );

    const makeHandleChangeActiveSport = useCallback(
        (sportId: string) => () => setActiveSport(sportId),
        []
    );

    const bannerZonesByPeriod = usePureSelector((state: State) =>
        createStructuredSelector(bannerZonesSelectorsByPeriod)(state, {})
    );

    const memBannerZonesByPeriod = useDeepMemo(bannerZonesByPeriod);

    const memZoneIdsByPeriod = useMemo(
        () =>
            transform(
                memBannerZonesByPeriod,
                (
                    acc,
                    bannerItems,
                    zoneId: keyof typeof memBannerZonesByPeriod
                ) => {
                    acc[zoneId] = bannerItems.map(({ id }) => id);
                }
            ) as { [key in WidgetType]: string[] },
        [memBannerZonesByPeriod]
    );

    const zonesByPeriod = usePureSelector((state: State) =>
        makeZonesSelectorByPeriods(memZoneIdsByPeriod)(state, {})
    );

    const memZonesByPeriod = useDeepMemo(zonesByPeriod);

    const periods = useMemo(
        () =>
            reduce(
                memZonesByPeriod,
                (acc, zoneIds, key: WidgetType) => {
                    const isWidgetTypeAvailable = some(
                        zoneIds,
                        matchIdsBySport => !isEmpty(matchIdsBySport)
                    );

                    if (isWidgetTypeAvailable) {
                        acc.push(key);
                    }

                    return acc;
                },
                [] as WidgetType[]
            ),
        [memZonesByPeriod]
    );

    const activeZones = activePeriod
        ? memZonesByPeriod[activePeriod]
        : undefined;

    const { sportEventIds, sportIds } = useMemo(
        () => getUniqSportsAndSportEventIds(activeZones, activeSport),
        [activeSport, activeZones]
    );

    const missingSportEventIds = usePureSelector((state: State) =>
        selectMissingSportEventIds(state, { sportEventIds })
    );

    const memMissingSportEventIds = useDeepMemo(missingSportEventIds);

    useEffect(() => {
        const firstPeriod = first(periods);

        if (!firstPeriod) return;

        setActivePeriod(firstPeriod);
    }, [periods]);

    useEffect(() => {
        flatten(values(memZoneIdsByPeriod))
            .filter(zoneId => !requestedZoneIdsCache.includes(zoneId))
            .forEach(zoneId => {
                requestedZoneIdsCache.push(zoneId);
                dispatch(
                    getBannersData({
                        zoneId,
                        marketStatuses: MARKETS_ACTIVE_STATUSES,
                        withSportEvent: false,
                        sportIds: getBettingOptions().sportIds,
                    })
                );
            });
    }, [dispatch, memZoneIdsByPeriod]);

    return {
        periods,
        sportIds,
        missingSportEventIds: memMissingSportEventIds,
        activeZones,
        activePeriod,
        activeSport,
        makeHandleActivePeriod,
        makeHandleChangeActiveSport,
    };
};

const bannerZonesSelectorsByPeriod = transform(
    attributesByPeriod,
    (acc, item, key: WidgetType) => {
        acc[key] = makeSelectBannerZonesByAttributes(item);
    }
) as {
    [key in WidgetType]: ReturnType<typeof makeSelectBannerZonesByAttributes>;
};

const makeZonesSelectorByPeriods = (
    data: { [key in WidgetType]: string[] }
) => {
    const selectProps = transform(data, (acc, zoneIds, period: WidgetType) => {
        acc[period] = makeSelectZones(zoneIds);
    }) as { [key in WidgetType]: ReturnType<typeof makeSelectZones> };

    return createStructuredSelector(selectProps);
};

const getUniqSportsAndSportEventIds = (
    activeZones: BannerItemsByZoneId | undefined,
    activeSport: string
): {
    sportEventIds: string[];
    sportIds: string[];
} => {
    const sportEventIds: string[] = [];
    const sportIds: string[] = [];

    if (!activeZones) {
        return {
            sportEventIds,
            sportIds,
        };
    }

    forEach(activeZones, matchIdsBySport => {
        if (!isEmpty(matchIdsBySport)) {
            const nextSportEventIds = get(
                matchIdsBySport,
                activeSport,
                undefined
            );

            if (nextSportEventIds) {
                sportEventIds.push(...Array.from(nextSportEventIds));
            }

            sportIds.push(...keys(matchIdsBySport));
        }
    });

    return {
        sportEventIds: uniq(sportEventIds),
        sportIds: uniq(sportIds),
    };
};

export default useMobileTopMatchList;
